import { AssetClassesQuery, DrawdownTargetPercentagesWithSubAssetClassesDto } from '@/__generated__/graphql';
import { AssetClassWithPrevValues } from '.';

export const getAllocationTargetsToUpdate = (assetClassTargets: AssetClassWithPrevValues[], targetSubAssetClasses: boolean) => {
  const hasSubAssetClasses = assetClassTargets.some((assetClass) => assetClass.subAssetClasses.length > 0);

  if (targetSubAssetClasses && hasSubAssetClasses) {
    return assetClassTargets.flatMap((assetClass) => {
      return assetClass.subAssetClasses.map((subAssetClass) => {
        return {
          subAssetClassId: subAssetClass.id,
          percentage: parseFloat(subAssetClass.percentage) || 0
        };
      });
    });
  }

  return assetClassTargets.map((assetClass) => ({
    assetClassId: assetClass.id,
    percentage: parseFloat(assetClass.percentage) || 0
  }));
};

export const sortByName = <T extends { name: string }>(arr: T[]) => {
  return [...arr].sort((a, b) => a.name.localeCompare(b.name));
};

export const structurAssetClassesWithPrevValues = (
  targets: DrawdownTargetPercentagesWithSubAssetClassesDto[],
  defaultAssetClasses: AssetClassesQuery['assetClasses']
): AssetClassWithPrevValues[] => {
  const structuredAssetClassesWithPrevValues = targets.map((assetClass) => {
    const defaultAssetClass = defaultAssetClasses.find((item) => item.id === assetClass.id);

    const structuredSubAssetClassesWithPrevValues = assetClass.subAssetClassDrawdownTargets.map((subAssetClass) => {
      const defaultSubAssetClass = defaultAssetClass?.subAssetClasses.find((item) => item.id === subAssetClass.id);
      return {
        id: subAssetClass.id,
        name: subAssetClass.name,
        defaultPercentage: `${defaultSubAssetClass?.defaultAllocationPercentage ?? '0'}%`,
        prevPercentage: `${subAssetClass.portfolioPercentage.toFixed(2)}%`,
        percentage: `${subAssetClass.portfolioPercentage.toFixed(2)}%`
      };
    });

    return {
      id: assetClass.id,
      name: assetClass.name,
      defaultPercentage: `${defaultAssetClass?.defaultAllocationPercentage ?? '0'}%`,
      prevPercentage: `${assetClass.portfolioPercentage.toFixed(2)}%`,
      percentage: `${assetClass.portfolioPercentage.toFixed(2)}%`,
      subAssetClasses: structuredSubAssetClassesWithPrevValues
    };
  });

  return structuredAssetClassesWithPrevValues;
};

export const calculatePrivateCapitalPercentage = (percentage: string, privatePercentage: string): number => {
  const calculatePercentageValue = (parseFloat(percentage) / parseFloat(privatePercentage)) * 100;
  return parseFloat(calculatePercentageValue?.toFixed(2)) || 0;
};

export const updateAssetClassDrawdownTargets = (
  targets: DrawdownTargetPercentagesWithSubAssetClassesDto[],
  changedAssetClassTargets: AssetClassWithPrevValues[],
  privatePercentage: string
): DrawdownTargetPercentagesWithSubAssetClassesDto[] => {
  const updatedAssetClassTargets = targets.map((assetClass) => {
    const currentAssetClass = changedAssetClassTargets.find((item) => item.id === assetClass.id);

    const updatedSubAssetClasses = Array.isArray(assetClass.subAssetClassDrawdownTargets)
      ? assetClass.subAssetClassDrawdownTargets.map((subAssetClass) => {
          const currentSubAssetClass = currentAssetClass?.subAssetClasses?.find((item) => item.id === subAssetClass.id);
          const percentage = currentSubAssetClass?.percentage ?? '0';
          return {
            ...subAssetClass,
            portfolioPercentage: parseFloat(percentage) || 0,
            privateCapitalPercentage: calculatePrivateCapitalPercentage(percentage, privatePercentage)
          };
        })
      : [];

    const sumSubAssetClassPercentages = updatedSubAssetClasses.reduce((acc, subAssetClass) => {
      return acc + (typeof subAssetClass?.portfolioPercentage === 'number' ? subAssetClass.portfolioPercentage : 0);
    }, 0);

    const hasSubAssetClasses = Array.isArray(currentAssetClass?.subAssetClasses) && currentAssetClass.subAssetClasses.length > 0;
    const assetClassPercentage = currentAssetClass?.percentage ?? '0';
    const portfolioPercentage = hasSubAssetClasses ? sumSubAssetClassPercentages : parseFloat(assetClassPercentage) || 0;

    return {
      ...assetClass,
      portfolioPercentage,
      privateCapitalPercentage: calculatePrivateCapitalPercentage(portfolioPercentage.toString(), privatePercentage),
      subAssetClassDrawdownTargets: updatedSubAssetClasses
    };
  });

  return updatedAssetClassTargets;
};

export const resetAssetClassTargets = (assetClassTargets: AssetClassWithPrevValues[]) => {
  return assetClassTargets.map((assetClass) => {
    return {
      ...assetClass,
      prevPercentage: assetClass.defaultPercentage,
      percentage: assetClass.defaultPercentage,
      subAssetClasses: assetClass.subAssetClasses.map((subAssetClass) => {
        return {
          ...subAssetClass,
          prevPercentage: subAssetClass.defaultPercentage,
          percentage: subAssetClass.defaultPercentage
        };
      })
    };
  });
};

export const getSumAssetClassPercentages = (assetClassTargets: AssetClassWithPrevValues[]): string => {
  return (
    assetClassTargets
      .reduce((acc, assetClass) => {
        return acc + parseFloat(assetClass.percentage);
      }, 0)
      .toFixed(2) + '%'
  );
};

/**
 * Combines duplicate asset classes in a list of drawdown targets.
 * For duplicate asset classes:
 * - Concatenates their sub asset classes
 * - Sums their portfolio and private capital percentages
 */
export const combineTargets = (targets: DrawdownTargetPercentagesWithSubAssetClassesDto[]): DrawdownTargetPercentagesWithSubAssetClassesDto[] => {
  const combinedTargetsMap = new Map<string, DrawdownTargetPercentagesWithSubAssetClassesDto>();

  targets.forEach((target) => {
    const existingTarget = combinedTargetsMap.get(target.id);

    if (existingTarget) {
      // Sum the percentages
      existingTarget.portfolioPercentage += target.portfolioPercentage;
      existingTarget.privateCapitalPercentage += target.privateCapitalPercentage;

      // Combine sub asset classes if they exist
      if (target.subAssetClassDrawdownTargets?.length) {
        existingTarget.subAssetClassDrawdownTargets = [
          ...(existingTarget.subAssetClassDrawdownTargets || []),
          ...target.subAssetClassDrawdownTargets
        ];
      }
    } else {
      // Create a deep copy of the target to avoid mutating the original
      combinedTargetsMap.set(target.id, {
        ...target,
        subAssetClassDrawdownTargets: [...(target.subAssetClassDrawdownTargets || [])]
      });
    }
  });

  return Array.from(combinedTargetsMap.values());
};
